import type {
  BodyUUID,
  CameraUUID,
  LightUUID,
  JointUUID,
  SceneCamera,
  SceneLight,
  Site,
  SiteKind,
  SiteUUID,
  Vector3,
  Vector4,
  WorldBody,
} from "../../types/scene";
import { generateUuid } from "../../utils/id";
import { rgbaToHexString } from "./attributeUtils";
import { normalizeQuat, parseElementOrientation } from "./orientation";
import type { ParsedBodiesMap, ParsedBodyNode, ParsedInertia } from "./types";
import { getDirectChildElementsByTag } from "./xmlUtils";

type BodyHierarchyParser = {
  xmlDoc: Document;
  angleUnit: "radian" | "degree";
  eulerseq: string;
  globalInertialCounter: number;
  jointNameToUuid: Record<string, JointUUID>;
  sitesMap: Record<string, Site>;
  camerasMap: Record<CameraUUID, SceneCamera>;
  lightsMap: Record<LightUUID, SceneLight>;
  coerceUuidFromMjcfName: (value: string | null) => string | null;
  getResolvedJointElementName: (jointElem: Element, fallbackIndex?: number) => string | null;
  parseBodyGeoms: (bodyElem: Element, name: string | null) => ParsedBodyNode["geoms"];
  parseCameraNode: (
    cameraElem: Element,
    defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
  ) => SceneCamera | null;
  parseLightNode: (
    lightElem: Element,
    defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
  ) => SceneLight | null;
  warn: (...args: unknown[]) => void;
};

export const resolveBodyUuidAndName = (
  parser: BodyHierarchyParser,
  bodyElem: Element
): {
  name: string | null;
  bodyUuid: BodyUUID;
  parsedBodyName: string;
} => {
  const name = bodyElem.getAttribute("name");
  const isUuidLikeBodyName = (value: string | null): boolean => {
    if (!value) return false;
    const s = String(value).trim();
    if (!s) return false;
    // Accept either a raw UUID, or our sanitized form (e.g. "b_<uuid>").
    const maybe = s.startsWith("b_") ? s.slice(2) : s;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(maybe);
  };
  const coerceBodyUuidFromName = (value: string | null): string | null => {
    if (!isUuidLikeBodyName(value)) return null;
    return parser.coerceUuidFromMjcfName(value);
  };
  const bodyUuid = (coerceBodyUuidFromName(name) || generateUuid()) as BodyUUID;
  const parsedBodyName = name || `body_${bodyUuid}`;
  return { name, bodyUuid, parsedBodyName };
};

export const parseBodyJointUuids = (
  parser: BodyHierarchyParser,
  bodyElem: Element,
  name: string | null
): JointUUID[] => {
  const jointElements = [
    ...getDirectChildElementsByTag(bodyElem, "joint"),
    ...getDirectChildElementsByTag(bodyElem, "freejoint"),
  ];
  return jointElements
    .map((j: Element, index: number) => {
      const resolvedName = parser.getResolvedJointElementName(j, index);
      if (!resolvedName) return null;
      const jointUuid = parser.jointNameToUuid[resolvedName];
      if (!jointUuid) {
        parser.warn(
          `[MJCFParser] Joint "${resolvedName}" referenced in body "${name}" but not found in parsed joints`
        );
        return null;
      }
      return jointUuid;
    })
    .filter((uuid): uuid is JointUUID => uuid !== null);
};

export const parseSiteElement = (
  parser: BodyHierarchyParser,
  siteElem: Element,
  fallbackName: string
): SiteUUID => {
  const siteName = siteElem.getAttribute("name")?.trim() || fallbackName;
  const pos = (siteElem
    .getAttribute("pos")
    ?.trim()
    .split(/\s+/)
    .map(Number)
    .map((v) => (Number.isFinite(v) ? v : 0)) ?? [0, 0, 0]) as Vector3;
  const quat = normalizeQuat(
    parseElementOrientation(siteElem, { eulerseq: parser.eulerseq, angleUnit: parser.angleUnit })
  );

  const size = (siteElem
    .getAttribute("size")
    ?.trim()
    .split(/\s+/)
    .map(Number)
    .map((v) => (Number.isFinite(v) && v > 0 ? v : 0.02)) ?? [0.02, 0.02, 0.02]) as Vector3;

  const kindAttr = siteElem.getAttribute("type") || "sphere";
  const rgbaAttr = siteElem.getAttribute("rgba");
  const materialAttr = siteElem.getAttribute("material");
  const color = rgbaToHexString(rgbaAttr) || "#ffff00";

  const stableUuid = parser.coerceUuidFromMjcfName(siteName);
  const uuid = (stableUuid as SiteUUID | null) || (generateUuid() as SiteUUID);
  const site: Site = {
    uuid,
    name: siteName,
    position: pos,
    orientation: quat,
    size,
    kind: kindAttr as SiteKind,
    color,
    material: materialAttr || undefined,
    hasExplicitRgba: Boolean(rgbaAttr),
  };

  parser.sitesMap[site.uuid] = site;

  return site.uuid;
};

export const parseBodySiteUuids = (
  parser: BodyHierarchyParser,
  bodyElem: Element,
  name: string | null
): SiteUUID[] => {
  let siteAutoCounter = 1;
  const siteElements = getDirectChildElementsByTag(bodyElem, "site");
  return siteElements.map((siteElem) =>
    parseSiteElement(parser, siteElem, `${name || "body"}_site_${siteAutoCounter++}`)
  );
};

export const parseBodyPlugins = (bodyElem: Element): string[] | undefined => {
  const serializer = new XMLSerializer();
  const plugins = getDirectChildElementsByTag(bodyElem, "plugin")
    .map((pluginElem) => serializer.serializeToString(pluginElem).trim())
    .filter((pluginXml): pluginXml is string => pluginXml.length > 0);
  return plugins.length > 0 ? plugins : undefined;
};

export const parseWorldbodySites = (parser: BodyHierarchyParser): void => {
  const worldbodies = Array.from(parser.xmlDoc.querySelectorAll("worldbody"));
  if (worldbodies.length === 0) return;
  let siteAutoCounter = 1;
  worldbodies.forEach((worldbody) => {
    getDirectChildElementsByTag(worldbody, "site").forEach((siteElem) => {
      parseSiteElement(parser, siteElem, `world_site_${siteAutoCounter++}`);
    });
  });
};

export const parseBodyCameras = (
  parser: BodyHierarchyParser,
  bodyElem: Element,
  bodyUuid: BodyUUID
): void => {
  const cameraElements = Array.from(
    bodyElem.querySelectorAll(":scope > camera") as NodeListOf<Element>
  );
  cameraElements.forEach((cameraElem) => {
    const parsed = parser.parseCameraNode(cameraElem, { kind: "mounted", mountBodyUuid: bodyUuid });
    if (parsed) parser.camerasMap[parsed.uuid] = parsed;
  });
};

export const parseBodyLights = (
  parser: BodyHierarchyParser,
  bodyElem: Element,
  bodyUuid: BodyUUID
): void => {
  const lightElements = Array.from(
    bodyElem.querySelectorAll(":scope > light") as NodeListOf<Element>
  );
  lightElements.forEach((lightElem) => {
    const parsed = parser.parseLightNode(lightElem, { kind: "mounted", mountBodyUuid: bodyUuid });
    if (parsed) parser.lightsMap[parsed.uuid] = parsed;
  });
};

export const parseBodyTransform = (
  parser: BodyHierarchyParser,
  bodyElem: Element
): { position: Vector3; orientation: Vector4 } => {
  const posRaw = bodyElem.getAttribute("pos")?.trim().split(/\s+/).map(Number);
  const position = (posRaw && posRaw.length >= 3 ? posRaw : [0, 0, 0]) as Vector3;
  const orientation = normalizeQuat(
    parseElementOrientation(bodyElem, { eulerseq: parser.eulerseq, angleUnit: parser.angleUnit })
  );
  return { position, orientation };
};

export const parseBodyInertia = (
  parser: BodyHierarchyParser,
  bodyElem: Element
): ParsedInertia | undefined => {
  const inertialElem = bodyElem.querySelector(":scope > inertial");
  return inertialElem
    ? (() => {
        const massAttr = inertialElem.getAttribute("mass");
        const posAttr = inertialElem.getAttribute("pos");
        const quatAttr = inertialElem.getAttribute("quat");
        const diagAttr = inertialElem.getAttribute("diaginertia");
        const fullAttr = inertialElem.getAttribute("fullinertia");
        const fullParsed = fullAttr
          ? (fullAttr.trim().split(/\s+/).map(Number) as [
              number,
              number,
              number,
              number,
              number,
              number,
            ])
          : undefined;
        return {
          uuid: generateUuid(),
          name: `inertial_${parser.globalInertialCounter++}`,
          mass: (() => {
            const m = massAttr ? parseFloat(massAttr) : 0;
            return Number.isFinite(m) && m > 0 ? m : 1;
          })(),
          position: (posAttr?.trim().split(/\s+/).map(Number) || [0, 0, 0]).map((v) =>
            Number.isFinite(v) ? v : 0
          ) as Vector3,
          orientation: (quatAttr?.trim().split(/\s+/).map(Number) || [1, 0, 0, 0]).map((v) =>
            Number.isFinite(v) ? v : 0
          ) as Vector4,
          diagonal: (diagAttr?.trim().split(/\s+/).map(Number) || null) as Vector3 | null,
          ...(fullParsed && fullParsed.length === 6 ? { fullinertia: fullParsed } : {}),
        };
      })()
    : undefined;
};

export const parseBodies = (rawParser: unknown, bodyElem: Element): ParsedBodyNode => {
  const parser = rawParser as BodyHierarchyParser;
  const { name, bodyUuid, parsedBodyName } = resolveBodyUuidAndName(parser, bodyElem);
  const geoms = parser.parseBodyGeoms(bodyElem, name);
  const joints = parseBodyJointUuids(parser, bodyElem, name);
  const siteUuids = parseBodySiteUuids(parser, bodyElem, name);
  const plugins = parseBodyPlugins(bodyElem);
  parseBodyCameras(parser, bodyElem, bodyUuid);
  parseBodyLights(parser, bodyElem, bodyUuid);

  const children = getDirectChildElementsByTag(bodyElem, "body").map((child: Element) =>
    parseBodies(rawParser, child)
  );

  const { position, orientation } = parseBodyTransform(parser, bodyElem);
  const inertia = parseBodyInertia(parser, bodyElem);

  const gravcompAttr = bodyElem.getAttribute("gravcomp");
  const gravityCompensation = (() => {
    if (gravcompAttr === null) return undefined;
    const v = parseFloat(gravcompAttr);
    return Number.isFinite(v) ? v : undefined;
  })();
  const mocapAttr = bodyElem.getAttribute("mocap");
  const isMocap = (() => {
    if (mocapAttr === null) return undefined;
    const normalized = String(mocapAttr).trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
    return undefined;
  })();
  const childclass = bodyElem.getAttribute("childclass") || undefined;
  const user = bodyElem.getAttribute("user") || undefined;

  return {
    uuid: bodyUuid,
    name: parsedBodyName,
    position,
    orientation,
    joints,
    sites: siteUuids,
    children,
    inertia,
    geoms,
    ...(gravityCompensation !== undefined && { gravityCompensation }),
    ...(isMocap !== undefined && { isMocap }),
    ...(childclass ? { childclass } : {}),
    ...(user ? { user } : {}),
    ...(plugins ? { plugins } : {}),
  };
};

export const flattenBodies = (
  bodies: ParsedBodyNode[]
): {
  bodiesMap: ParsedBodiesMap;
  worldBodies: WorldBody[];
} => {
  const bodiesMap: ParsedBodiesMap = {};
  const worldBodies: WorldBody[] = [];

  const flatten = (body: ParsedBodyNode): BodyUUID => {
    const uuid = body.uuid;
    const childUUIDs: BodyUUID[] = [];

    if (body.children && body.children.length > 0) {
      body.children.forEach((child) => {
        const childUUID = flatten(child);
        childUUIDs.push(childUUID);
      });
    }

    bodiesMap[uuid] = {
      ...body,
      children: childUUIDs,
    };

    return uuid;
  };

  const usedNames = new Set<string>();
  bodies.forEach((body) => {
    const uuid = flatten(body);
    let number = 1;
    let bodyName = `Object ${number}`;
    while (usedNames.has(bodyName)) {
      number += 1;
      bodyName = `Object ${number}`;
    }
    usedNames.add(bodyName);
    worldBodies.push({ uuid: uuid as WorldBody["uuid"], name: bodyName });
  });

  return { bodiesMap, worldBodies };
};

export const getBodies = (
  rawParser: unknown
): { bodiesMap: ParsedBodiesMap; worldBodies: WorldBody[] } => {
  const parser = rawParser as BodyHierarchyParser;
  parseWorldbodySites(parser);
  const worldCameras = Array.from(
    parser.xmlDoc.querySelectorAll("worldbody > camera") as NodeListOf<Element>
  );
  worldCameras.forEach((cameraElem) => {
    const parsed = parser.parseCameraNode(cameraElem, { kind: "fixed" });
    if (parsed) parser.camerasMap[parsed.uuid] = parsed;
  });
  const worldLights = Array.from(
    parser.xmlDoc.querySelectorAll("worldbody > light") as NodeListOf<Element>
  );
  worldLights.forEach((lightElem) => {
    const parsed = parser.parseLightNode(lightElem, { kind: "fixed" });
    if (parsed) parser.lightsMap[parsed.uuid] = parsed;
  });
  const bodyTrees = Array.from(
    parser.xmlDoc.querySelectorAll("worldbody > body") as NodeListOf<Element>
  ).map((body) => parseBodies(rawParser, body));
  return flattenBodies(bodyTrees);
};
